// PO1-3083 (épico PO1-3076): página pública da transportadora no Leilão
// Reverso de Frete — /public/leilao-frete/:token. Sem login: o token do link é
// o acesso. Visual da Cotação Spot (cabeçalho, pill de contagem regressiva,
// máscara de R$); carregamento e erros no padrão do Convite da Transportadora.
//
// Leilão fechado (P3): a página mostra só os PRÓPRIOS lances. A contagem
// regressiva usa a hora do servidor (serverNow), e o lance depois do fim é
// recusado pelo backend (409 janela_encerrada) mesmo que o relógio local
// discorde. Polling de 30 s enquanto a janela está aberta.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, CardBody, Col, Input, Row, Spinner } from "reactstrap";
import { contextoLeilao, enviarLance, recusarLeilao } from "services/leilaoFretePublicApi";
import QuemEstaContratando from "components/gobid/QuemEstaContratando"; // PO1-3528 (épico PO1-3527)
import autopilotLogo from "../../../assets/img/autopilot-logo.png";
import { countdown, fmtBRL, fmtData, fmtDataHora, formatBRLInput, offsetDoServidor, parseBRL } from "../../Empresa/GoBID/Leiloes/regras";
import { fmtLimite } from "utils/limitesColetaEntrega"; // PO1-3481

const POLL_MS = 30000;

const SHELL_STYLE = {
  minHeight: "100vh",
  background: "var(--everlog-card-hero-bg)",
  padding: "32px 16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
};
// PO1-3481: faixa de urgência no cabeçalho do convite.
const URGENTE_STYLE = {
  display: "inline-block",
  marginTop: 10,
  padding: "4px 12px",
  borderRadius: 999,
  background: "var(--everlog-status-danger, #ec250d)",
  color: "#fff",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.5,
};
const BRAND_HEADER_STYLE = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 24 };
const BRAND_LEFT_STYLE = { display: "flex", alignItems: "center", gap: 12 };
const BRAND_LOGO_STYLE = { width: 44, height: 44, objectFit: "contain" };
const BRAND_TEXT_STYLE = { color: "var(--everlog-card-title)", fontSize: 22, fontWeight: 600, letterSpacing: 0.3 };
const FOOTER_STYLE = { marginTop: 32, textAlign: "center", color: "var(--everlog-card-text-soft)", fontSize: 12, letterSpacing: 0.3 };
const FOOTER_LINK_STYLE = { color: "var(--theme-accent)", textDecoration: "none", fontWeight: 500 };
const CARD_STYLE = {
  borderRadius: 12,
  boxShadow: "var(--everlog-card-elevated-shadow)",
  border: "1px solid var(--everlog-card-border-soft)",
  background: "var(--everlog-card-surface-elevated)",
  overflow: "hidden",
};
const HEADER_STYLE = {
  background: "linear-gradient(135deg, var(--theme-accent-surface, var(--theme-accent)), var(--theme-accent-surface-strong, var(--theme-accent-strong)))",
  color: "var(--everlog-on-accent-text)",
  padding: "20px 28px",
};
const BOX_STYLE = {
  background: "var(--everlog-card-surface-section)",
  border: "1px solid var(--everlog-card-border-soft)",
  borderRadius: 8,
  padding: "12px 16px",
  marginBottom: 20,
};
const LABEL_STYLE = { fontSize: 12, color: "var(--everlog-card-muted)" };
const VALUE_STYLE = { fontWeight: 600, color: "var(--everlog-card-title)" };
const TEXT_STYLE = { color: "var(--everlog-card-text)" };
const OBSERVACOES_STYLE = {
  marginTop: 12,
  padding: "10px 14px",
  borderRadius: 8,
  borderLeft: "4px solid var(--everlog-status-warning)",
  background: "color-mix(in srgb, var(--everlog-status-warning) 10%, transparent)",
};
const OBSERVACOES_TITULO_STYLE = { fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--everlog-status-warning)", marginBottom: 4 };
const MUTED_STYLE = { color: "var(--everlog-card-muted)", fontSize: 13 };
const PILL_BASE = {
  borderRadius: 999,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  whiteSpace: "nowrap",
  letterSpacing: 0.2,
  fontVariantNumeric: "tabular-nums",
};

function pillStyle(level) {
  const token = level === "danger" ? "danger" : level === "warning" ? "warning" : "success";
  return {
    ...PILL_BASE,
    color: `var(--everlog-status-${token})`,
    background: `color-mix(in srgb, var(--everlog-status-${token}) 14%, transparent)`,
    border: `1px solid var(--everlog-status-${token})`,
  };
}

const ERRO_MAP = {
  token_invalido: "Link inválido. Confira se copiou a URL completa do e-mail.",
  token_expirado: "Este link expirou. Peça ao embarcador para reenviar o convite.",
  rate_limited: "Muitas tentativas. Aguarde alguns minutos e tente de novo.",
  janela_encerrada: "A janela deste leilão já fechou.",
  lance_nao_melhora: "O novo lance precisa ser menor que o seu lance atual.",
  dados_invalidos: "Confira o valor e o prazo informados.",
};

const Shell = ({ children, pill }) => (
  <div style={SHELL_STYLE}>
    <div style={{ width: "100%", maxWidth: 760 }}>
      <div style={BRAND_HEADER_STYLE}>
        <div style={BRAND_LEFT_STYLE}>
          <img src={autopilotLogo} alt="" style={BRAND_LOGO_STYLE} aria-hidden="true" />
          <span style={BRAND_TEXT_STYLE}>Everlog</span>
        </div>
        {pill ? (
          <span style={pillStyle(pill.level)} title={pill.title}>
            <span aria-hidden="true">⏱</span> {pill.texto}
          </span>
        ) : null}
      </div>
      {children}
      <div style={FOOTER_STYLE}>
        Este leilão foi publicado pela plataforma{" "}
        <a href="https://www.everlogbrasil.com.br" target="_blank" rel="noopener noreferrer" style={FOOTER_LINK_STYLE}>
          Everlog
        </a>
        . Em caso de dúvida, responda ao e-mail do convite.
      </div>
    </div>
  </div>
);

function KV({ label, children }) {
  return (
    <Col xs={6} md={4} style={{ marginBottom: 10 }}>
      <div style={LABEL_STYLE}>{label}</div>
      <div style={VALUE_STYLE}>{children}</div>
    </Col>
  );
}

export default function LeilaoFrete() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [offset, setOffset] = useState(0);
  const [, setTick] = useState(0);
  const [valor, setValor] = useState("");
  const [prazo, setPrazo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState(null);
  const [sucesso, setSucesso] = useState(null);
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const fetchRef = useRef(null);

  const carregar = useCallback(
    async (silencioso) => {
      if (!silencioso) setLoading(true);
      try {
        const r = await contextoLeilao(token);
        setCtx(r);
        setOffset(offsetDoServidor(r.serverNow));
        setErro(null);
      } catch (err) {
        const status = err.response && err.response.status;
        const data = (err.response && err.response.data) || {};
        setErro(status === 429 ? "rate_limited" : data.code || "token_invalido");
      } finally {
        if (!silencioso) setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    fetchRef.current = carregar;
  }, [carregar]);
  useEffect(() => {
    carregar(false);
  }, [carregar]);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const estado = ctx && ctx.estado;
  useEffect(() => {
    if (estado !== "aberta" && estado !== "encerrada") return undefined;
    const p = setInterval(() => {
      if (document.visibilityState === "visible" && fetchRef.current) fetchRef.current(true);
    }, POLL_MS);
    return () => clearInterval(p);
  }, [estado]);

  const cd = useMemo(() => (ctx ? countdown(ctx.fechaEm, offset) : null), [ctx, offset]);
  const janelaAberta = Boolean(ctx && ctx.estado === "aberta" && cd && cd.ms > 0);

  const aplicar = (r, msg) => {
    setCtx(r);
    setOffset(offsetDoServidor(r.serverNow));
    setSucesso(msg);
    setErroEnvio(null);
    setValor("");
    setPrazo("");
    setRecusando(false);
  };

  const tratarErroEnvio = (err) => {
    const status = err.response && err.response.status;
    const data = (err.response && err.response.data) || {};
    const detalhe = data.errors ? Object.keys(data.errors).map((k) => data.errors[k]).join(" ") : "";
    setErroEnvio(`${status === 429 ? ERRO_MAP.rate_limited : ERRO_MAP[data.code] || data.error || "Não foi possível enviar."}${detalhe ? ` ${detalhe}` : ""}`);
    if (data.code === "janela_encerrada") carregar(true);
  };

  const handleAceitarAlvo = async () => {
    const p = Number(prazo);
    if (!(p >= 1)) {
      setErroEnvio("Informe o prazo de entrega em dias para aceitar o alvo.");
      return;
    }
    setEnviando(true);
    setErroEnvio(null);
    try {
      const r = await enviarLance(token, { aceitarAlvo: true, prazoDias: p });
      aplicar(r, `Alvo aceito: ${fmtBRL(r.lanceVigente && r.lanceVigente.valor)} em ${p} dia(s). Confirmação enviada por e-mail.`);
    } catch (err) {
      tratarErroEnvio(err);
    } finally {
      setEnviando(false);
    }
  };

  const handleEnviar = async () => {
    const v = parseBRL(valor);
    const p = Number(prazo);
    if (!(v > 0) || !(p >= 1)) {
      setErroEnvio("Informe o valor do frete e o prazo de entrega em dias.");
      return;
    }
    setEnviando(true);
    setErroEnvio(null);
    try {
      const r = await enviarLance(token, { valor: v, prazoDias: p });
      aplicar(r, `Lance registrado: ${fmtBRL(v)} em ${p} dia(s). Confirmação enviada por e-mail.`);
    } catch (err) {
      tratarErroEnvio(err);
    } finally {
      setEnviando(false);
    }
  };

  const handleRecusar = async () => {
    setEnviando(true);
    setErroEnvio(null);
    try {
      const r = await recusarLeilao(token, { motivo: motivo || undefined });
      aplicar(r, "Obrigado por avisar. O embarcador foi informado de que você não vai participar.");
    } catch (err) {
      tratarErroEnvio(err);
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <Card style={CARD_STYLE}>
          <CardBody style={{ textAlign: "center", padding: 48, ...TEXT_STYLE }}>
            <Spinner color="primary" />
            <p style={{ marginTop: 16, ...MUTED_STYLE }}>Carregando o leilão…</p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  if (erro && !ctx) {
    return (
      <Shell>
        <Card style={CARD_STYLE}>
          <CardBody style={{ padding: 32 }}>
            <h4 style={{ color: "var(--everlog-status-danger)", marginBottom: 12 }}>Não foi possível abrir este leilão</h4>
            <p style={TEXT_STYLE}>{ERRO_MAP[erro] || "Algo deu errado. Tente novamente em instantes."}</p>
          </CardBody>
        </Card>
      </Shell>
    );
  }

  const { carga, embarcador, convidado, meusLances, lanceVigente, resultado, vencedor, limites } = ctx;
  const pill = ctx.estado === "aberta" ? { ...cd, title: `Fecha em ${fmtDataHora(ctx.fechaEm)}` } : { texto: "Janela encerrada", level: "danger", title: `Fechou em ${fmtDataHora(ctx.fechaEm)}` };

  let resultadoTexto = null;
  let resultadoTone = "info";
  if (resultado === "venceu") {
    resultadoTexto = `Sua transportadora venceu com ${fmtBRL(vencedor && vencedor.valor)} em ${vencedor && vencedor.prazoDias} dia(s).${vencedor && vencedor.aguardandoAprovacao ? " A escolha ainda passa pela aprovação interna do embarcador; a coleta chega depois dela." : " O embarcador vai entrar em contato para a coleta."}`;
    resultadoTone = "success";
  } else if (resultado === "nao_venceu") {
    resultadoTexto = "Este leilão foi encerrado e outra transportadora foi escolhida desta vez. Obrigado por participar.";
    resultadoTone = "warning";
  } else if (resultado === "aguardando") {
    resultadoTexto = meusLances.length ? "A janela fechou. O embarcador está escolhendo o vencedor; você recebe o resultado por e-mail." : "A janela fechou sem lance da sua transportadora.";
  } else if (resultado === "deserta") {
    resultadoTexto = "A janela fechou sem nenhum lance. O embarcador pode reabrir o leilão com outro preço-alvo.";
  } else if (resultado === "cancelada") {
    resultadoTexto = "Este leilão foi cancelado pelo embarcador.";
  }

  return (
    <Shell pill={pill}>
      <Card style={CARD_STYLE}>
        <div style={HEADER_STYLE}>
          <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: 1 }}>
            LEILÃO REVERSO DE FRETE Nº {ctx.numero} · CLIENTE SOLICITANTE
          </div>
          {/* Quem pede o frete em destaque; a convidada já sabe quem ela é. */}
          <h3 style={{ margin: "4px 0 0", fontWeight: 700 }}>{embarcador.nome || "Embarcador"}</h3>
          <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>Convite para {convidado.nome || "sua transportadora"}</div>
          {carga.urgente ? (
            <div style={URGENTE_STYLE} role="note">
              URGENTE · coleta nas próximas 24 horas{carga.coletarAte ? ` · coletar até ${fmtLimite(carga.coletarAte)}` : ""}
            </div>
          ) : null}
        </div>
        <CardBody style={{ padding: "28px 32px" }}>
          <p style={{ ...TEXT_STYLE, marginBottom: 16 }}>
            Preço-alvo do embarcador: <strong style={{ fontSize: 18 }}>{fmtBRL(ctx.precoAlvo)}</strong>. Você pode aceitar o alvo ou propor um valor menor, com o prazo de entrega. Vence o menor valor
            recebido até <strong>{fmtDataHora(ctx.fechaEm)}</strong>; em empate, quem respondeu primeiro.
          </p>

          <div style={BOX_STYLE}>
            <Row>
              <KV label="Origem">
                {carga.origem.cidade}/{carga.origem.uf}
              </KV>
              <KV label="Destino">
                {carga.destino.cidade}/{carga.destino.uf}
              </KV>
              {carga.coletarAte ? (
                <KV label="Coletar até">{fmtLimite(carga.coletarAte)}</KV>
              ) : (
                <KV label="Coleta">{fmtData(carga.dataColeta)}</KV>
              )}
              {carga.entregarAte ? <KV label="Entregar até">{fmtLimite(carga.entregarAte)}</KV> : null}
              <KV label="Peso">{carga.pesoBruto ? `${carga.pesoBruto} kg` : "—"}</KV>
              <KV label="Volume">{carga.volume ? `${carga.volume} m³` : carga.qtdVolumes ? `${carga.qtdVolumes} volume(s)` : "—"}</KV>
              <KV label="Valor da NF">{fmtBRL(carga.valorNF)}</KV>
              {carga.tipoVeiculo ? <KV label="Veículo">{carga.tipoVeiculo}</KV> : null}
            </Row>
            {carga.observacoes ? (
              <div style={OBSERVACOES_STYLE}>
                <div style={OBSERVACOES_TITULO_STYLE}>Observações do embarcador</div>
                <div style={{ ...TEXT_STYLE, color: "var(--everlog-card-title)", whiteSpace: "pre-line" }}>{carga.observacoes}</div>
              </div>
            ) : null}
          </div>

          {/* PO1-3528: só vem preenchido para convidada de fora da carteira. */}
          <QuemEstaContratando perfil={ctx.perfilEmbarcador} />

          {resultadoTexto ? (
            <div
              style={{
                ...BOX_STYLE,
                borderColor: `var(--everlog-status-${resultadoTone})`,
                background: `color-mix(in srgb, var(--everlog-status-${resultadoTone}) 10%, transparent)`,
                color: "var(--everlog-card-title)",
              }}
            >
              {resultadoTexto}
            </div>
          ) : null}

          {meusLances.length ? (
            <div style={BOX_STYLE}>
              <div style={{ ...LABEL_STYLE, marginBottom: 6 }}>Seus lances</div>
              {meusLances
                .slice()
                .reverse()
                .map((l, i) => (
                  <div key={l.id} style={{ ...TEXT_STYLE, fontWeight: i === 0 ? 600 : 400, opacity: i === 0 ? 1 : 0.7 }}>
                    {fmtBRL(l.valor)} · {l.prazoDias} dia(s) · {fmtDataHora(l.criadoEm)}
                    {i === 0 ? " · vigente" : " · superado"}
                  </div>
                ))}
            </div>
          ) : null}

          {sucesso ? (
            <div style={{ ...BOX_STYLE, borderColor: "var(--everlog-status-success)", color: "var(--everlog-status-success)" }}>{sucesso}</div>
          ) : null}

          {janelaAberta ? (
            <>
              <h5 style={{ marginBottom: 12, color: "var(--everlog-card-title)" }}>{lanceVigente ? "Melhorar o lance" : "Dar o lance"}</h5>
              {lanceVigente ? <p style={MUTED_STYLE}>Seu lance atual é {fmtBRL(lanceVigente.valor)}; o novo precisa ser menor.</p> : null}
              <Row>
                <Col md={5}>
                  <div style={LABEL_STYLE}>Valor do frete (R$)</div>
                  <Input inputMode="decimal" placeholder="0,00" value={valor} onChange={(e) => setValor(formatBRLInput(e.target.value))} disabled={enviando} />
                </Col>
                <Col md={3}>
                  <div style={LABEL_STYLE}>Prazo (dias)</div>
                  <Input type="number" min={limites.prazoMinDias} max={limites.prazoMaxDias} value={prazo} onChange={(e) => setPrazo(e.target.value)} disabled={enviando} />
                </Col>
                <Col md={4} style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
                  <Button color="primary" onClick={handleEnviar} disabled={enviando}>
                    {enviando ? "Enviando..." : "Enviar lance"}
                  </Button>
                  {!lanceVigente ? (
                    <Button color="success" outline onClick={handleAceitarAlvo} disabled={enviando} title="Registra o lance exatamente no preço-alvo">
                      Aceitar o alvo
                    </Button>
                  ) : null}
                </Col>
              </Row>
              {erroEnvio ? <p style={{ color: "var(--everlog-status-danger)", marginTop: 10 }}>{erroEnvio}</p> : null}

              {convidado.estado !== "recusou" && !lanceVigente ? (
                <div style={{ marginTop: 24, borderTop: "1px solid var(--everlog-card-divider)", paddingTop: 16 }}>
                  {!recusando ? (
                    <Button color="danger" outline size="sm" onClick={() => setRecusando(true)} disabled={enviando}>
                      Não vou participar
                    </Button>
                  ) : (
                    <Row>
                      <Col md={8}>
                        <Input placeholder="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} disabled={enviando} />
                      </Col>
                      <Col md={4} style={{ display: "flex", gap: 8 }}>
                        <Button color="danger" outline onClick={handleRecusar} disabled={enviando}>
                          Confirmar
                        </Button>
                        <Button color="secondary" outline onClick={() => setRecusando(false)} disabled={enviando}>
                          Voltar
                        </Button>
                      </Col>
                    </Row>
                  )}
                </div>
              ) : null}
            </>
          ) : null}
        </CardBody>
      </Card>
    </Shell>
  );
}
